import * as React from 'react';
import PropTypes from 'prop-types';
import { Input } from '@mui/base/Input';
import { Box, styled } from '@mui/system';

function OTP({ separator, length, value, onChange, isSecondaryMarket }) {
  const inputRefs = React.useRef(new Array(length).fill(null));

  const focusInput = (targetIndex) => {
    const targetInput = inputRefs.current[targetIndex];
    targetInput.focus();
  };

  const selectInput = (targetIndex) => {
    const targetInput = inputRefs.current[targetIndex];
    targetInput.select();
  };

  const handleKeyDown = (event, currentIndex) => {
    switch (event.key) {
      case 'Enter':
        if (currentIndex === length - 1) {
          event.currentTarget.blur();
        }
        break;
      case 'ArrowUp':
      case 'ArrowDown':
      case ' ':
        event.preventDefault();
        break;
      case 'ArrowLeft':
        event.preventDefault();
        if (currentIndex > 0) {
          focusInput(currentIndex - 1);
          selectInput(currentIndex - 1);
        }
        break;
      case 'ArrowRight':
        event.preventDefault();
        if (currentIndex < length - 1) {
          focusInput(currentIndex + 1);
          selectInput(currentIndex + 1);
        }
        break;
      case 'Delete':
        event.preventDefault();
        onChange((prevOtp) => {
          const otp = prevOtp.slice(0, currentIndex) + prevOtp.slice(currentIndex + 1);
          return otp;
        });

        break;
      case 'Backspace':
        event.preventDefault();
        if (currentIndex > 0) {
          focusInput(currentIndex - 1);
          selectInput(currentIndex - 1);
        }

        onChange((prevOtp) => {
          const otp = prevOtp.slice(0, currentIndex) + prevOtp.slice(currentIndex + 1);
          return otp;
        });
        break;

      default:
        break;
    }
  };

  const handleChange = (event, currentIndex) => {
    let currentValue = event.target.value.replace(/\D/g, '');

    if (!currentValue) return;

    // Mobile keyboard paste / OTP autofill
    if (currentValue.length > 1) {
      const otpArray = value.split('');

      for (
        let i = 0;
        i < currentValue.length && currentIndex + i < length;
        i++
      ) {
        otpArray[currentIndex + i] = currentValue[i];
      }

      onChange(otpArray.join(''));

      const nextIndex = Math.min(
        currentIndex + currentValue.length,
        length - 1
      );

      if (nextIndex === length - 1) {
        inputRefs.current[nextIndex]?.blur();
      } else {
        focusInput(nextIndex);
      }

      return;
    }

    // Single digit typing
    onChange((prev) => {
      const otpArray = prev.split('');
      otpArray[currentIndex] = currentValue;
      return otpArray.join('');
    });

    if (currentIndex < length - 1) {
      focusInput(currentIndex + 1);
    } else {
      event.currentTarget.blur();
    }
  };

  const handleClick = (event, currentIndex) => {
    selectInput(currentIndex);
  };

  const handlePaste = (event, currentIndex) => {
    event.preventDefault();
    const clipboardData = event.clipboardData;

    // Check if there is text data in the clipboard
    if (clipboardData.types.includes('text/plain')) {
      let pastedText = clipboardData.getData('text/plain');
      pastedText = pastedText.replace(/\D/g, '').substring(0, length).trim();
      let indexToEnter = 0;

      while (indexToEnter <= currentIndex) {
        if (inputRefs.current[indexToEnter].value && indexToEnter < currentIndex) {
          indexToEnter += 1;
        } else {
          break;
        }
      }

      const otpArray = value.split('');

      for (let i = indexToEnter; i < length; i += 1) {
        const lastValue = pastedText[i - indexToEnter] ?? ' ';
        otpArray[i] = lastValue;
      }

      onChange(otpArray.join(''));
    }
  };

  return (
    <Box sx={{ display: 'flex', gap: isSecondaryMarket ? 2.5 : 1, justifyContent: 'center', alignItems: 'center' }}>
      {new Array(length).fill(null).map((_, index) => (
        <React.Fragment key={index}>
          <Input
            placeholder='-'
            slots={{
              input: isSecondaryMarket ? SecondaryMarketInputElement : InputElement,
            }}
            aria-label={`Digit ${index + 1} of OTP`}
            slotProps={{
              input: {
                ref: (ele) => {
                  inputRefs.current[index] = ele;
                },
                onKeyDown: (event) => handleKeyDown(event, index),
                onChange: (event) => handleChange(event, index),
                onClick: (event) => handleClick(event, index),
                onPaste: (event) => handlePaste(event, index),
                value: value[index] ?? '',
                inputMode: 'numeric',
                autoComplete: 'one-time-code',
                pattern: '[0-9]*',
                maxLength: length,
              },
            }}
          />
          {index === length - 1 ? null : separator}
        </React.Fragment>
      ))}
    </Box>
  );
}

OTP.propTypes = {
  length: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  separator: PropTypes.node,
  value: PropTypes.string.isRequired,
};

export default function OTPField({ otpChange, length, isSecondaryMarket = false }) {
  const [otp, setOtp] = React.useState('');

  React.useEffect(() => {
    otpChange(otp);
  }, [otp]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <OTP value={otp} onChange={setOtp} length={length ?? 4} isSecondaryMarket={isSecondaryMarket} />
    </Box>
  );
}

const grey = {
  50: '#F3F6F9',
  100: '#E5EAF2',
  200: '#DAE2ED',
  300: '#C7D0DD',
  400: '#B0B8C4',
  500: '#9DA8B7',
  600: '#6B7A90',
  700: '#434D5B',
  800: '#303740',
  900: '#1C2025',
};

const InputElement = styled('input')(
  ({ theme }) => `
  width: 45px;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.5;
  padding: 11px 0;
  outline: none;
  border-radius: 5px;
  text-align: center;
  color: ${theme.palette.mode === 'dark' ? grey[300] : grey[900]};
  background: ${theme.palette.mode === 'dark' ? grey[900] : '#fff'};
  border: 1px solid ${grey[200]};
  box-shadow: 0 2px 4px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0, 0.5)' : 'rgba(0,0,0, 0.05)'};

  &:hover {
    border-color: #B4A085;
  }

  &:focus {
    border-color: #B4A085;
  }

  /* firefox */
  &:focus-visible {
    outline: 0;
  }
`,
);

const SecondaryMarketInputElement = styled('input')(
  ({ theme }) => `
  width: 55px;
  font-family: 'IBM Plex Sans', sans-serif;
  font-size: 0.875rem;
  font-weight: 400;
  line-height: 1.5;
  padding: 16px 0;
  outline: none;
  border-radius: 5px;
  text-align: center;
  color: ${theme.palette.mode === 'dark' ? grey[300] : grey[900]};
  background: ${theme.palette.mode === 'dark' ? grey[900] : '#fff'};
  border: 1px solid ${grey[200]};
  box-shadow: 0 2px 4px ${theme.palette.mode === 'dark' ? 'rgba(0,0,0, 0.5)' : 'rgba(0,0,0, 0.05)'};

  &:hover {
    border-color: #B4A085;
  }

  &:focus {
    border-color: #B4A085;
  }

  /* firefox */
  &:focus-visible {
    outline: 0;
  }

`,
);
